import { useRef, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, LineReveal } from '@/components/feature/AnimatedSection';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

const companyInfo = [
  { label: '会社名', value: 'メセナインターコム株式会社' },
  { label: '英文社名', value: 'mecena intercom inc.' },
  { label: '代表取締役', value: '宮園篤雄' },
  { label: '所在地', value: '〒150-0036 東京都渋谷区南平台町15-10\n南平台1510ビル 5階' },
  { label: 'TEL', value: '03-3405-9741' },
  { label: 'FAX', value: '03-3401-6503' },
  { label: '営業時間', value: '平日 10:00〜19:00\n土日祝 定休' },
  {
    label: '事業内容',
    value: '・広告・PRに関する各種メディア扱い及び企画・制作\n・販売促進及びキャンペーンに関する企画・制作\n・カタログ、ポスター等、印刷物の企画・制作\n・映像コンテンツの企画・制作\n・イベント、展示会、博覧会等各種催事の企画・制作・実施・運営\n・講演会、研修会の企画・設計・施工・管理\n・CIに関する企画・制作',
  },
];

const values = [
  { en: 'Honesty', jp: '誠実さ', body: '「できないこと」は正直に伝えます。できることに、全力を尽くします。' },
  { en: 'Dialogue', jp: '対話', body: '答えを持ち込まず、組織の内側にある意味を一緒に見つけます。' },
  { en: 'Long-term', jp: '長期視点', body: '3年後、5年後に振り返ったとき、「あのとき正しかった」と思える仕事を。' },
  { en: 'Legacy', jp: '資産化', body: 'プロジェクトが終わっても、組織に残る力を育てることを意識します。' },
];

const messageParagraphs = [
  '医療・福祉・教育・地域・文化の現場には、深い専門性と誠実な実践があります。しかし、その価値が社会に届いていないことが、あまりにも多い。',
  '「情報はある。実績もある。努力もある。それでも届かない。」この言葉を、私は何度も現場で聞いてきました。',
  '問題は、活動の質ではありません。「意味」が言語化されていないこと。「価値」が構造化されていないこと。それが、届かない本当の理由です。',
  '私たちは、その「意味」を掘り起こし、言語化し、社会に届く構造を設計します。速く、派手に、目立つことを目的とせず、3年後も誇れる言葉を一緒に残すことを大切にしています。',
  '意味は、すでにそこにある。私たちは、それを見えるようにするお手伝いをするだけです。',
];

// 会社概要テーブル行をスタッガーで表示
function CompanyInfoTable() {
  const { ref, visibleCount } = useStaggerAnimation(companyInfo.length, { threshold: 0.05 });
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-0 border-t border-divider">
      {companyInfo.map((item, idx) => (
        <div
          key={item.label}
          className="grid grid-cols-5 gap-4 py-5 border-b border-divider group hover:bg-warm-white/50 transition-colors duration-200 -mx-2 px-2"
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateX(0)' : 'translateX(-16px)',
            transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <span className="col-span-2 font-sans-jp text-xs text-muted-light tracking-widest-jp">{item.label}</span>
          <span className="col-span-3 font-sans-jp text-sm text-charcoal leading-relaxed-jp whitespace-pre-line">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// 代表メッセージ段落をスタッガーで表示
function MessageParagraphs() {
  const { ref, visibleCount } = useStaggerAnimation(messageParagraphs.length, { threshold: 0.05 });
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-6">
      {messageParagraphs.map((p, idx) => (
        <p
          key={idx}
          className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp"
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {p}
        </p>
      ))}
    </div>
  );
}

// ヒーローの各要素を時間差で表示
function HeroContent() {
  const { ref: lineRef, isVisible: lineVis } = useScrollAnimation({ threshold: 0.01, rootMargin: '0px' });
  const { ref: h1Ref, isVisible: h1Vis } = useScrollAnimation({ threshold: 0.01, rootMargin: '0px' });
  const { ref: pRef, isVisible: pVis } = useScrollAnimation({ threshold: 0.01, rootMargin: '0px' });

  return (
    <div className="md:col-span-8 md:col-start-3">
      {/* ラベル行 */}
      <div
        ref={lineRef as React.RefObject<HTMLDivElement>}
        className="flex items-center gap-4 mb-10"
        style={{
          opacity: lineVis ? 1 : 0,
          transform: lineVis ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s',
        }}
      >
        <LineReveal delay={0.2} color="bg-accent-teal" className="w-6" />
        <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Company</span>
      </div>

      {/* H1 */}
      <h1
        ref={h1Ref as React.RefObject<HTMLHeadingElement>}
        className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12 overflow-hidden"
        style={{
          opacity: h1Vis ? 1 : 0,
          transform: h1Vis ? 'translateY(0)' : 'translateY(36px)',
          transition: 'opacity 0.85s cubic-bezier(0.22,1,0.36,1) 0.3s, transform 0.85s cubic-bezier(0.22,1,0.36,1) 0.3s',
        }}
      >
        会社情報
      </h1>

      {/* Description */}
      <p
        ref={pRef as React.RefObject<HTMLParagraphElement>}
        className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl"
        style={{
          opacity: pVis ? 1 : 0,
          transform: pVis ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.5s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.5s',
        }}
      >
        mecena Intercomは、医療・福祉・教育・地域・文化の領域で、<br />
        組織の意味設計・価値創造・市場創造を行うコンサルティングファームです。
      </p>
    </div>
  );
}

export default function CompanyPage() {
  useSEO({
    title: '会社情報 | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社の会社概要。東京都渋谷区南平台に拠点を置き、医療・福祉・教育・地域・文化の意味設計・価値創造・市場創造を行うコンサルティングファームです。代表取締役：宮園篤雄。',
    keywords: 'メセナインターコム,会社概要,渋谷区南平台,意味設計,コンサルティング,宮園篤雄',
    ogTitle: '会社情報 | メセナインターコム株式会社',
    ogDescription: 'メセナインターコム株式会社の会社概要。東京都渋谷区南平台に拠点を置き、医療・福祉・教育・地域・文化の意味設計・価値創造を行うコンサルティングファームです。',
    ogUrl: 'https://mecena-intercom.co.jp/company',
    canonical: 'https://mecena-intercom.co.jp/company',
  });

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-28 md:pt-52 md:pb-40 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="gold" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <HeroContent />
            </div>
          </div>
        </section>

        {/* Company info */}
        <section className="texture-company py-20 md:py-32 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-16">

              {/* 左：会社概要テーブル */}
              <FadeIn delay={0} from="left" className="md:col-span-5">
                <div className="flex items-center gap-4 mb-8">
                  <LineReveal delay={0.2} color="bg-accent-teal" className="w-6" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Profile</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl mb-10">会社概要</h2>
                <CompanyInfoTable />
              </FadeIn>

              {/* 右：大切にしていること */}
              <FadeIn delay={0.15} from="right" className="md:col-span-7">
                <div className="flex items-center gap-4 mb-8">
                  <LineReveal delay={0.3} color="bg-accent-teal" className="w-6" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Values</span>
                </div>
                <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl mb-10">大切にしていること</h2>
                <StaggerContainer
                  items={values}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-divider border border-divider"
                  renderItem={(v, _idx, isVis) => (
                    <div
                      className="bg-warm-light p-8 flex flex-col gap-4 card-lift cursor-default"
                      style={{
                        opacity: isVis ? 1 : 0,
                        transform: isVis ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                        transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      <div>
                        <span className="font-garamond text-xs italic text-muted-light tracking-widest-jp block mb-1">{v.en}</span>
                        <span className="font-serif-jp text-xl font-light text-charcoal tracking-normal-jp">{v.jp}</span>
                      </div>
                      <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{v.body}</p>
                    </div>
                  )}
                />
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Message */}
        <section className="texture-message py-20 md:py-32 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-8 md:col-start-3">
                <FadeIn delay={0}>
                  <div className="flex items-center gap-4 mb-10">
                    <LineReveal delay={0.2} color="bg-accent-teal" className="w-6" />
                    <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Message</span>
                  </div>
                  <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl mb-12">
                    代表メッセージ
                  </h2>
                </FadeIn>

                {/* 段落ごとにスタッガー */}
                <MessageParagraphs />

                <FadeIn delay={0.1}>
                  <div className="mt-12 pt-8 border-t border-divider flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                    <div>
                      <p className="font-serif-jp font-light text-charcoal tracking-normal-jp text-lg">代表取締役　宮園篤雄</p>
                      <p className="font-garamond text-xs italic text-muted-light mt-1">Representative Director, mecena intercom inc.</p>
                    </div>
                    <Link
                      to="/message"
                      className="inline-flex items-center gap-2 text-sm font-sans-jp text-accent-teal tracking-wide-jp hover:opacity-70 transition-opacity duration-300 whitespace-nowrap flex-shrink-0 hover-underline"
                    >
                      <span>メッセージ全文を読む</span>
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-20 md:py-32 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0} from="bottom" distance={40}>
              <div className="flex items-center gap-4 mb-8">
                <LineReveal delay={0.2} color="bg-accent-teal" className="w-6" />
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Access</span>
              </div>
              <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl mb-4">アクセス</h2>
              <div className="w-full overflow-hidden" style={{ height: '480px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.748057773498!2d139.69656731524658!3d35.65437997971!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188c80fe40af8f%3A0xa4ab06d3249d187a!2z44Oh44K744OK44Kk44Oz44K_44O844Kz44Og5qCq5byP5Lya56S-!5e0!3m2!1sja!2sjp!4v1700000000000!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="メセナインターコム株式会社 地図"
                />
              </div>
            </FadeIn>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-white border-t border-divider">
          <FadeIn delay={0} from="bottom" distance={32}>
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                  一緒に、意味をつくりませんか。
                </h3>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                  まずは30分、現状の「意味」を一緒に整理しましょう。
                </p>
              </div>
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap flex-shrink-0">
                相談してみる
              </Link>
            </div>
          </FadeIn>
        </section>
      </main>
      <Footer />
    </div>
  );
}
